import React, { ReactNode, useEffect, useState } from 'react';

import { observer } from 'mobx-react';
import { Button, Surface } from '@infoods/lifebase-ui';

import classes from './RequiredAttentionWidget.module.scss';
import { WidgetTitle } from '../../components/WidgetTitle/WidgetTitle';
import {
  LbTabPanel,
  LbTabs,
  LbUnderlineTabsList,
  TabsConfig,
} from '../../../shared/components/tabs';
import { UnreadScansTable } from './UnreadScansTable/UnreadScansTable';
import { PaymentIssueTable } from './PaymentIssueTable/PaymentIssueTable';
import { PreRegisteredTable } from './PreRegisteredTable/PreRegisteredTable';
import { unreadScansStore } from '../../stores/unreadScansStore';
import { paymentIssueStore } from '../../stores/paymentIssueStore';
import { preRegisteredStore } from '../../stores/preRegisteredStore';
import { SolutionTypeFilter } from '../../../shared/constants/user-info/solutionType';
import { UnreadScansTableMode } from './UnreadScansTable/UnreadScansTable.settings';
import { PaymentIssueTableMode } from './PaymentIssueTable/PaymentIssueTable.settings';

type RequiredAttentionWidgetProps = {
  /** Filters data loaded into Unread Scans and Payment Issue tables */
  solutionType: SolutionTypeFilter;
  /** Controls which columns are visible in the Unread Scans table */
  unreadScansTableMode: UnreadScansTableMode;
  /** Controls which columns are visible in the Payment Issue table */
  paymentIssueTableMode: PaymentIssueTableMode;
  /** show only for ALL or NUTRITION solution type */
  showPreRegisteredTable?: boolean;
  titleIcon?: ReactNode;
  titleTooltipContent?: ReactNode;
};

type TabTypes = 'unread-scans' | 'payment-issue' | 'pre-registered';

const RequiredAttentionWidget = observer(
  ({
    solutionType,
    unreadScansTableMode,
    paymentIssueTableMode,
    showPreRegisteredTable,
    titleTooltipContent,
    titleIcon,
  }: RequiredAttentionWidgetProps) => {
    const [activeTab, setActiveTab] = useState<TabTypes>('unread-scans');

    useEffect(() => {
      unreadScansStore.getUnreadScans({
        solutionType,
      });
      paymentIssueStore.getPaymentIssues({
        solutionType,
      });

      if (showPreRegisteredTable) {
        preRegisteredStore.getPreRegisteredList();
      }

      return () => {
        unreadScansStore.setDefaultValues();
        paymentIssueStore.setDefaultValues();
        preRegisteredStore.setDefaultValues();
      };
    }, [solutionType]);

    // redirect to default tab if active table is not supported
    useEffect(() => {
      if (!showPreRegisteredTable && activeTab === 'pre-registered') {
        setActiveTab('unread-scans');
      }
    }, [showPreRegisteredTable, activeTab]);

    const handleMarkAllAsRead = async () => {
      await unreadScansStore.readAllScans(solutionType);
    };

    const tabsConfigs: TabsConfig[] = [
      {
        label: `Unread Scans (${unreadScansStore.filterMethods.totalElements})`,
        value: 'unread-scans',
        panel: <UnreadScansTable tableMode={unreadScansTableMode} />,
      },
      {
        label: `Payment Issue (${paymentIssueStore.filterMethods.totalElements})`,
        value: 'payment-issue',
        panel: <PaymentIssueTable tableMode={paymentIssueTableMode} />,
      },

      {
        label: `Pre-Registered (${preRegisteredStore.filterMethods.totalElements})`,
        value: 'pre-registered',
        panel: <PreRegisteredTable />,
        hide: !showPreRegisteredTable,
      },
    ];

    return (
      <Surface radius="l" className={classes.container}>
        <WidgetTitle
          className={classes.title}
          title="Requires Attention"
          icon={titleIcon}
          tooltipContent={titleTooltipContent}
          endAdornment={
            activeTab === 'unread-scans' && (
              <Button variant="text" size="small" onClick={handleMarkAllAsRead}>
                Mark All as Read
              </Button>
            )
          }
        />
        <LbTabs value={activeTab} onValueChange={setActiveTab}>
          <LbUnderlineTabsList className={classes.tabs} tabsConfig={tabsConfigs} />
          <LbTabPanel tabsConfig={tabsConfigs} />
        </LbTabs>
      </Surface>
    );
  },
);

export { RequiredAttentionWidget };
